import { useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// AbortController ref for cancelling streams

export interface EssayChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

export interface SavedChat {
  id: string;
  title: string;
  updated_at: string;
  messages: EssayChatMessage[];
  context_analysis_ids: string[];
}

export function useEssayChat() {
  const [messages, setMessages] = useState<EssayChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [contextAnalysisIds, setContextAnalysisIds] = useState<string[]>([]);
  const [savedChats, setSavedChats] = useState<SavedChat[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesRef = useRef<EssayChatMessage[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Fetch all user's essay chats (global + legacy per-analysis)
  const refreshChatList = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('essay_chats' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (data) {
        setSavedChats(data.map((chat: any) => ({
          id: chat.id,
          title: chat.title,
          updated_at: chat.updated_at || chat.created_at,
          messages: Array.isArray(chat.messages) ? chat.messages : [],
          context_analysis_ids: Array.isArray(chat.context_analysis_ids) ? chat.context_analysis_ids : [],
        })));
      }
    } catch (err) {
      console.error("Failed to load chat list:", err);
    }
  }, []);

  // Load the most recent global chat (analysis_id IS NULL)
  const loadChat = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('essay_chats' as any)
        .select('*')
        .eq('user_id', user.id)
        .is('analysis_id', null)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const chat = data[0] as any;
        setChatId(chat.id);
        setMessages(Array.isArray(chat.messages) ? chat.messages : []);
        setContextAnalysisIds(
          Array.isArray(chat.context_analysis_ids) ? chat.context_analysis_ids : []
        );
      }
    } catch (err) {
      console.error("Failed to load chat:", err);
    }
  }, []);

  useEffect(() => {
    loadChat();
    refreshChatList();
  }, [loadChat, refreshChatList]);

  // Load a specific chat by ID
  const loadChatById = useCallback((id: string) => {
    const chat = savedChats.find(c => c.id === id);
    if (!chat) return;
    setChatId(chat.id);
    setMessages(chat.messages);
    setContextAnalysisIds(chat.context_analysis_ids);
  }, [savedChats]);

  // Save chat (debounced)
  const saveChat = useCallback(async (msgs?: EssayChatMessage[]) => {
    const toSave = msgs || messagesRef.current;
    if (toSave.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const chatData = {
        user_id: user.id,
        analysis_id: null as any,
        messages: toSave as any,
        context_analysis_ids: contextAnalysisIds,
        title: `Essay Chat`,
        updated_at: new Date().toISOString(),
      };

      if (chatId) {
        await (supabase.from('essay_chats' as any) as any)
          .update(chatData)
          .eq('id', chatId);
      } else {
        const { data } = await (supabase.from('essay_chats' as any) as any)
          .insert(chatData)
          .select('id')
          .single();
        if (data) setChatId(data.id);
      }
      await refreshChatList();
    } catch (err) {
      console.error("Failed to save chat:", err);
    }
  }, [chatId, contextAnalysisIds, refreshChatList]);

  const debouncedSave = useCallback((msgs: EssayChatMessage[]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChat(msgs), 1000);
  }, [saveChat]);

  // Send message with streaming
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMsg: EssayChatMessage = {
      role: "user",
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };

    const updatedMessages = [...messagesRef.current, userMsg];
    setMessages(updatedMessages);
    setIsLoading(true);

    let assistantContent = "";

    try {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in to use essay chat");
        setIsLoading(false);
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/essay-chat`,
        {
          method: "POST",
          signal: abortController.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: content.trim(),
            analysisId: contextAnalysisIds[0] || "",
            contextAnalysisIds,
            conversationHistory: messagesRef.current.slice(-40).map(m => ({
              role: m.role,
              content: m.content,
            })),
          }),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        if (errorData.code === "INVALID_USER_KEY") {
          toast.error("Your API key appears to be invalid or expired. Update it in Settings.");
        } else if (resp.status === 429) {
          toast.error(errorData.error || "Too many requests, please wait a moment");
        } else if (resp.status === 402) {
          toast.error("Usage limit reached. Please upgrade your plan.");
        } else {
          toast.error(errorData.error || "Failed to get AI response");
        }
        setIsLoading(false);
        return;
      }

      if (!resp.body) {
        toast.error("No response received");
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, {
                  role: "assistant" as const,
                  content: assistantContent,
                  timestamp: new Date().toISOString(),
                }];
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Flush remaining
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, {
                  role: "assistant" as const,
                  content: assistantContent,
                  timestamp: new Date().toISOString(),
                }];
              });
            }
          } catch { /* ignore */ }
        }
      }

      // Save after stream completes
      const finalMessages = [
        ...updatedMessages,
        { role: "assistant" as const, content: assistantContent, timestamp: new Date().toISOString() },
      ];
      debouncedSave(finalMessages);
    } catch (err: any) {
      if (err?.name === "AbortError") {
        // User cancelled — save what we have so far
      } else {
        console.error("Stream error:", err);
        toast.error("Failed to connect. Please try again.");
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
      // Save whatever content we got (including partial from cancel)
      const currentMsgs = messagesRef.current;
      if (currentMsgs.length > 0) {
        debouncedSave(currentMsgs);
      }
    }
  }, [isLoading, contextAnalysisIds, debouncedSave]);

  const addContextAnalysis = useCallback((id: string) => {
    if (contextAnalysisIds.includes(id)) return;
    if (contextAnalysisIds.length >= 3) {
      toast.error("Maximum 3 analyses for context");
      return;
    }
    setContextAnalysisIds(prev => [...prev, id]);
  }, [contextAnalysisIds]);

  const removeContextAnalysis = useCallback((id: string) => {
    setContextAnalysisIds(prev => prev.filter(cId => cId !== id));
  }, []);

  const startNewChat = useCallback(async () => {
    if (messagesRef.current.length > 0) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      await saveChat();
    }
    setMessages([]);
    messagesRef.current = [];
    setChatId(null);
    setContextAnalysisIds([]);
  }, [saveChat]);

  const deleteChat = useCallback(async () => {
    if (chatId) {
      await (supabase.from('essay_chats' as any) as any)
        .delete()
        .eq('id', chatId);
    }
    setMessages([]);
    messagesRef.current = [];
    setChatId(null);
    setContextAnalysisIds([]);
    await refreshChatList();
    toast.success("Chat deleted");
  }, [chatId, refreshChatList]);

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const saveImmediate = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveChat();
  }, [saveChat]);

  return {
    messages,
    isLoading,
    chatId,
    contextAnalysisIds,
    savedChats,
    sendMessage,
    cancelStream,
    addContextAnalysis,
    removeContextAnalysis,
    startNewChat,
    deleteChat,
    saveImmediate,
    loadChatById,
  };
}
